import { createWithEqualityFn as create } from 'zustand/traditional';
import { shallow } from 'zustand/vanilla/shallow';

import CollectionService from 'src/services/CollectionService';

import useProjectsStore from './projects';

import type {
    TCollections,
    ICollectionsChatBundles,
    ICollectionsChatBundleRequest,
    ICollectionsChatBundle,
} from 'src/types/Collections';

interface IState {
    publicCollections: ICollectionsChatBundles;

    privateCollection: ICollectionsChatBundle<false>;

    cities: string[];
}

interface IActions {
    setCollections: (collections: TCollections) => void;

    getCollections: (city?: string, tags?: string[]) => Promise<void>;

    updateCollection: (collection: ICollectionsChatBundleRequest) => Promise<void>;

    getPrivateCollection: () => Promise<ICollectionsChatBundle<false>>;
    clear: () => void;
}

const useCollectionsStore = create<IState & IActions>(
    (set, get) => ({
        publicCollections: [],

        privateCollection: {} as ICollectionsChatBundle<false>,

        cities: [],
        clear: () =>
            set({
                publicCollections: [],
                privateCollection: {} as ICollectionsChatBundle<false>,
                cities: [],
            }),
        setCollections: ({ chat_bundles, cities, locations }) => {
            const { publicCollections, privateCollections } = chat_bundles.reduce(
                (acc, item) => {
                    if (item.is_public) {
                        acc.publicCollections.push(item);
                    } else {
                        acc.privateCollections.push(item);
                    }
                    return acc;
                },
                {
                    publicCollections: [] as ICollectionsChatBundles,
                    privateCollections: [] as ICollectionsChatBundles,
                }
            );
            const normalizedCities = cities && cities.length > 0 ? cities : locations || [];

            // Не перезаписываем privateCollection, если она уже загружена и содержит чаты
            // Это предотвращает потерю данных при вызове getCollections
            const currentPrivateCollection = get().privateCollection;
            const hasLoadedPrivateCollection =
                currentPrivateCollection?.id && currentPrivateCollection?.chats !== undefined;

            const newPrivateCollection = privateCollections[0]
                ? (privateCollections[0] as unknown as ICollectionsChatBundle<false>)
                : undefined;

            set({
                publicCollections,
                privateCollection: hasLoadedPrivateCollection
                    ? currentPrivateCollection
                    : newPrivateCollection || currentPrivateCollection,
                cities: normalizedCities,
            });
        },

        getCollections: async (city, tags) => {
            try {
                const data = await CollectionService.getAllCollections(city, tags);
                get().setCollections(data);
                return Promise.resolve();
            } catch (err) {
                return Promise.reject(err);
            }
        },

        getPrivateCollection: async () => {
            try {
                const activeProjectId = useProjectsStore.getState().activeProject?.id;

                if (!activeProjectId) {
                    throw new Error('Нет активного проекта');
                }

                const data = await CollectionService.getPrivateCollection(activeProjectId);

                // Если подборка не найдена, возвращаем пустую подборку вместо ошибки
                if (!data.chat_bundles || !data.chat_bundles[0]) {
                    const emptyCollection = {
                        id: undefined,
                        name: '',
                        chats: [],
                        is_public: false,
                    } as unknown as ICollectionsChatBundle<false>;
                    set({
                        privateCollection: emptyCollection,
                    });
                    return Promise.resolve(emptyCollection);
                }
                set({
                    privateCollection: data.chat_bundles[0] as unknown as ICollectionsChatBundle<false>,
                });
                return Promise.resolve(get().privateCollection);
            } catch (err) {
                // В случае ошибки также возвращаем пустую подборку
                const emptyCollection = {
                    id: undefined,
                    name: '',
                    chats: [],
                    is_public: false,
                } as unknown as ICollectionsChatBundle<false>;
                set({
                    privateCollection: emptyCollection,
                });
                return Promise.resolve(emptyCollection);
            }
        },

        updateCollection: async (collection: ICollectionsChatBundleRequest) => {
            try {
                const data = await CollectionService.updateCollection(collection);

                set({
                    privateCollection: data as unknown as ICollectionsChatBundle<false>,
                });

                return Promise.resolve();
            } catch (error) {
                return Promise.reject(error);
            }
        },
    }),
    shallow
);

export default useCollectionsStore;
